import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  invoiceCreationBlockedTooltipKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { useAuth } from "@/contexts/AuthContext";
import { NuevaFacturaDialog } from "@/pages/invoices/NuevaFacturaDialog";
import { DashboardTrustStrip } from "@/pages/dashboard/DashboardTrustStrip";
import { DashboardViewControl } from "@/pages/dashboard/DashboardViewControl";
import { LG_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import type { DashboardView } from "@/hooks/useDashboardView";

interface DashboardHeaderProps {
  /** From React Query when dashboard data is loaded; shows "Última actualización". */
  dataUpdatedAt?: number;
  /**
   * Current layout — drives the greeting-vs-status header copy. The toggle
   * control itself now lives in the app header (components/shared/Header),
   * so it is reachable from every route rather than only from /dashboard.
   */
  view?: DashboardView;
}

export function DashboardHeader({
  dataUpdatedAt,
  view,
}: DashboardHeaderProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isDesktop = useMediaQuery(LG_QUERY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const disabledTooltipKey = invoiceCreationBlockedTooltipKey(invoicingBlockedReason);

  // Simple view mirrors the mobile Inicio header: a greeting eyebrow and a
  // short editorial title, with no subtitle claim. Advanced keeps the
  // original copy.
  const isSimple = view === "simple";
  const title = isSimple
    ? t("dashboard.simpleTitle", "Tu día en Fiscalo.")
    : t("dashboard.title");
  const { lead, accent } = splitTitleAccent(title);

  const firstName = (
    user?.firstName ??
    user?.displayName?.split(" ")[0] ??
    ""
  ).trim();
  const todayLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "short",
  })
    .format(new Date())
    .replace(/\./g, "");
  // No first name (invited user who never filled their profile) degrades to
  // a date-only eyebrow rather than rendering "Hola  · viernes 29 ago".
  const eyebrow = isSimple
    ? firstName
      ? t("dashboard.greetingEyebrow", "Hola {{name}} · {{date}}", {
          name: firstName,
          date: todayLabel,
        })
      : todayLabel
    : t("dashboard.eyebrow", "Estado actual · hoy");

  return (
    <div className="fx-phead">
      <div className="fx-phead-text">
        <span className="fx-eyebrow">{eyebrow}</span>
        <h1>
          {lead}
          {accent && (
            <>
              {" "}
              <span className="serif">{accent}</span>
            </>
          )}
        </h1>
        {isSimple ? null : <p className="sub">{t("dashboard.subtitle")}</p>}
        {dataUpdatedAt != null && dataUpdatedAt > 0 ? (
          <DashboardTrustStrip dataUpdatedAt={dataUpdatedAt} />
        ) : null}
      </div>
      <div className="fx-phead-actions">
        {invoicingBlocked && disabledTooltipKey ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <button
                  type="button"
                  disabled
                  className="fx-btn fx-btn-accent"
                  aria-disabled="true"
                >
                  <Plus className="h-4 w-4" />
                  {t("dashboard.header.createInvoice")}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t(disabledTooltipKey)}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="fx-btn fx-btn-accent"
            onClick={() => setPickerOpen(true)}
          >
            <Plus className="h-4 w-4" />
            {t("dashboard.header.createInvoice")}
          </button>
        )}
      </div>

      {/* Mobile home for the view toggle. The topbar cannot hold it on a
          phone — its two labels come to ~234px, which overlapped the logo.
          Here it gets full width, and it sits directly above the cards it
          rearranges. Desktop keeps it in the topbar, so it stays reachable
          from every route. Exactly one of the two mounts (see Header). */}
      {!isDesktop && (
        <div className="fx-phead-view">
          <DashboardViewControl placement="block" />
        </div>
      )}

      <NuevaFacturaDialog open={pickerOpen} onOpenChange={setPickerOpen} />
    </div>
  );
}

function splitTitleAccent(title: string): { lead: string; accent: string | null } {
  const parts = title.trim().split(/\s+/);
  if (parts.length < 2) return { lead: title, accent: null };
  const accent = parts[parts.length - 1];
  const lead = parts.slice(0, -1).join(" ");
  return { lead, accent };
}
